import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage, FormattedNumber } from 'react-intl';
import { Button, Card, Icon, Popover } from '@salesforce/design-system-react';
import { PredictedWinnerBadge } from '../';
import './ExperimentDetails.scss';

class ExperimentDetails extends Component {
  getI18nID(suffix) {
    return `components.experiment.performance.results.${suffix}`;
  }

  renderPredictedWinnerBadge() {
    const status = this.props.experiment?.status;
    const winner =
      this.props.experiment?.winner?.uuid ||
      this.props?.performers?.[0]?.selectable?.uuid;
    if (
      winner &&
      winner === this.props.experiment?.suggested_winner &&
      (status === 'PUBLISHED' || status === 'FINISHED')
    ) {
      return <PredictedWinnerBadge />;
    } else {
      return null;
    }
  }

  renderTopPerformer() {
    const { formatMessage } = this.props.intl;
    const performerLabel =
      this.props.experiment?.status === 'PUBLISHED'
        ? 'topPerformer'
        : 'winningPerformer';
    const lockIcon = (
      <Icon
        className="icon"
        assistiveText={{ label: 'Lock' }}
        category="utility"
        colorVariant="light"
        name="priority"
        size="x-small"
        style={{ fill: 'green' }}
      />
    );
    const zeroCtor = this.props.performers[0].ctor === 0;

    if (this.props.experiment?.status === 'DRAFT' || zeroCtor) {
      return (
        <div>
          <p className="title">
            {lockIcon}
            <FormattedMessage id={this.getI18nID('topPerformer')} />
          </p>
          <span className="performer-name slds-col slds-size_2-of-2">
            {formatMessage({
              id: this.getI18nID('nonePerformer')
            })}
          </span>
        </div>
      );
    }
    if (this.props.performers?.length < 1) {
      return null;
    }

    const declaredWinner = this.props.experiment?.winner;
    const performer = declaredWinner || this.props.performers[0].selectable;

    return (
      <div>
        <div className="leader-badges">
          <p className="title">
            {lockIcon}
            <FormattedMessage id={this.getI18nID(`${performerLabel}`)} />
          </p>
        </div>
        <div className="leader-badges">{this.renderPredictedWinnerBadge()}</div>
        <div>
          <Popover
            className={'thumbnail-expanded-popover'}
            heading={performer.name}
            body={
              <div className="popover-body">
                <img
                  className="thumbnail-cell-popover-img"
                  src={performer.image_url}
                  alt={formatMessage(
                    {
                      id: 'components.ecs.popover.image.altText'
                    },
                    {
                      name: performer.name
                    }
                  )}
                />
                <div className="popover-text slds-grid slds-gutters">
                  <span className="popover-label slds-col slds-size_1-of-2">
                    {formatMessage({
                      id: 'components.experiment.assetBrowser.name'
                    })}
                  </span>
                  <span className="popover-value slds-col slds-size_2-of-2">
                    {performer.name}
                  </span>
                </div>
              </div>
            }
            align="right top"
            position="overflowBoundaryElement"
          >
            <Button variant="base">
              <div
                className="thumbnail-container slds-col"
                style={{ backgroundImage: `url(${performer.image_url})` }}
              />
            </Button>
          </Popover>
          <span className="performer-name slds-col slds-size_2-of-2">
            {performer.name}
          </span>
        </div>
      </div>
    );
  }

  render() {
    const { formatNumber } = this.props.intl;
    const isDraft = this.props.experiment?.status === 'DRAFT';
    const assetCount = isDraft
      ? this.props.experiment.content_test_assets.length || 0
      : this.props.summary?.total_selectables || 0;
    const selectionCount = this.props.summary?.all_selections || 0;
    const clickCount = this.props.summary?.all_clicks || 0;
    const avgCTOR = formatNumber(this.props.summary?.avg_ctor || 0, {
      style: 'percent',
      maximumFractionDigits: 2
    });

    if (this.props.performers.length === 0 && !isDraft) {
      return null;
    }
    return (
      <div className="experiment-details-overview">
        <Card hasNoHeader>
          <div className="experiment-details-container slds-grid slds-gutters">
            <div className="experiment-performance slds-col slds-size_2-of-6">
              <div className="details-header">
                <h2 className="header">
                  <FormattedMessage id={this.getI18nID('title')} />
                </h2>
                {this.renderTopPerformer()}
              </div>
            </div>
            <div className="experiment-performance-content slds-col slds-size_4-of-6">
              <div className="slds-grid">
                <div className="slds-col slds-size_1-of-4 stat-block">
                  <p className="title">
                    <FormattedMessage id={this.getI18nID('assets')} />
                  </p>
                  <p className="count">
                    <FormattedNumber value={assetCount} />
                  </p>
                </div>
                <div className="slds-col slds-size_1-of-4 stat-block">
                  <p className="title">
                    <FormattedMessage id={this.getI18nID('selections')} />
                  </p>
                  <p className="count">
                    <FormattedNumber value={selectionCount} />
                  </p>
                </div>
                <div className="slds-col slds-size_1-of-4 stat-block">
                  <p className="title">
                    <FormattedMessage id={this.getI18nID('clicks')} />
                  </p>
                  <p className="count">
                    <FormattedNumber value={clickCount} />
                  </p>
                </div>
                <div className="slds-col slds-size_1-of-4 stat-block">
                  <p className="title">
                    <FormattedMessage id={this.getI18nID('ctor')} />
                  </p>
                  <p className="count">{avgCTOR}</p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }
}

ExperimentDetails.propTypes = {
  actions: PropTypes.object,
  performers: PropTypes.array,
  details: PropTypes.array,
  experiment: PropTypes.object,
  filter: PropTypes.object.isRequired,
  jobs: PropTypes.array,
  summary: PropTypes.object,
  location: PropTypes.object
};

export default injectIntl(ExperimentDetails);
